import { useMemo } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { F0Form, f0FormField, useF0Form } from '@factorialco/f0-react'
import {
  AtsEditHiringPipelineApplicationPhaseDocument,
  AtsGetHiringPipelinePhasesDocument,
  AtsGetHiringPipelineStagesDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getEditableStages } from 'modules/ats/lib/utils/hiringStages'

import { EditApplicationPhaseSkeleton } from './EditApplicationPhaseSkeleton'

interface Props {
  id: string
  name: string
  hiringStageId?: string | null
  onClose: () => void
}

export const EditApplicationPhase = ({ onClose, id, name, hiringStageId }: Props) => {
  const { loading: loadingHiringStages, data: hiringStagesData } = useQuery(
    AtsGetHiringPipelineStagesDocument
  )

  const [edit, { loading: loadingSave }] = useMutation(
    AtsEditHiringPipelineApplicationPhaseDocument,
    {
      extractorFn: (data) => data.atsMutations.updateAtsApplicationPhase,
      refetchQueries: [AtsGetHiringPipelinePhasesDocument],
    }
  )

  const { formRef, submit, isSubmitting } = useF0Form()

  const hiringStages = useMemo(
    () => hiringStagesData?.ats.hiringStagesConnection.nodes || [],
    [hiringStagesData]
  )

  const schema = useMemo(
    () =>
      z.object({
        hiringStageId: f0FormField(
          z
            .string()
            .min(1, i18n.t('common.empty'))
            .refine((value) => value !== '0', {
              message: i18n.t('common.option_selected.zero'),
            }),
          {
            label: i18n.t('ats.job_posting_settings.hiring_pipeline.edit_phase_modal.stage_label'),
            fieldType: 'select',
            options: getEditableStages(hiringStages).map((stage) => ({
              label: stage.label,
              value: stage.id,
            })),
          }
        ),
        name: f0FormField(z.string().min(1, i18n.t('common.empty')), {
          label: i18n.t(
            'ats.job_posting_settings.hiring_pipeline.edit_phase_modal.phase_name_label'
          ),
          fieldType: 'text',
        }),
      }),
    [hiringStages]
  )

  return (
    <Modal
      title={i18n.t('ats.job_posting_settings.hiring_pipeline.edit_phase_modal.title')}
      illustration="automations"
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" onClick={() => submit()} loading={loadingSave || isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      {loadingHiringStages || !hiringStages ? (
        <EditApplicationPhaseSkeleton />
      ) : (
        <F0Form
          formRef={formRef}
          name="ats-edit-application-phase"
          schema={schema}
          defaultValues={{
            name: name,
            hiringStageId: hiringStageId ?? '',
          }}
          submitConfig={{ type: 'default', hideSubmitButton: true, hideActionBar: true }}
          onSubmit={async (data) => {
            const result = await edit({
              variables: {
                id: id,
                atsHiringStageId: data.hiringStageId,
                name: data.name,
              },
            })

            const fieldErrors: Record<string, string> = {}
            const handled = handleResult(result, (errors) => {
              Object.assign(fieldErrors, errors)
            })

            if (handled.success) {
              app.setFlash({
                type: 'correct',
                message: i18n.t('common.saved_successfully'),
              })
              onClose()
              return { success: true }
            }

            return Object.keys(fieldErrors).length > 0
              ? { success: false, errors: fieldErrors }
              : { success: false }
          }}
        />
      )}
    </Modal>
  )
}
